import { FC, Suspense, useEffect, useState } from "react";
import axios from "axios";
import { BASE_URL } from "./configs";
import { mainRoutes } from "./routes";
import { useRoutes } from "react-router-dom";
import { MD5 } from "crypto-js";
import { APP_KEY } from "./configs";
import "./assets/styles/rest.less";
import "./index.less";
import { message } from "antd";
import { getToken, reactConfig } from "./utils";
import { heantenant } from "./api";
import { HeantenantModel } from "./model";
import { useFetch } from "./hooks";

// 🔴 每次发版修改这个值
const APP_VERSION = "1.0.2"; // 发版时手动更新版本号

const BLANK_FAVICON =
  "data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg'/%3E";

const clearFavicon = () => {
  document
    .querySelectorAll(
      "link[rel='icon'], link[rel='shortcut icon'], link[rel='apple-touch-icon'], link[rel='apple-touch-icon-precomposed']"
    )
    .forEach((node) => node.remove());

  const icon = document.createElement("link");
  icon.rel = "icon";
  icon.type = "image/svg+xml";
  icon.href = BLANK_FAVICON;
  document.head.appendChild(icon);
};

axios.defaults.baseURL = BASE_URL;

const App: FC<{}> = () => {
  const [shouldRefresh, setShouldRefresh] = useState(false);

  const applyPageConfig = () => {
    clearFavicon();
    document.title = reactConfig().title;
  };

  // 🔴 版本检查逻辑：只在新版本发布时刷新一次
  useEffect(() => {
    const checkVersion = () => {
      const storedVersion = localStorage.getItem("app_version");

      if (storedVersion !== APP_VERSION) {
        console.log(`检测到新版本: ${storedVersion} -> ${APP_VERSION}`);
        setShouldRefresh(true);
        localStorage.setItem("app_version", APP_VERSION);
      }
    };

    checkVersion();
  }, []);

  // 🔴 如果检测到新版本，刷新页面
  useEffect(() => {
    if (shouldRefresh) {
      const timer = setTimeout(() => {
        window.location.reload();
      }, 100);

      return () => clearTimeout(timer);
    }
  }, [shouldRefresh]);

  const { run: tenantDetailvoid } = useFetch<HeantenantModel["tenantDetail"]>(
    heantenant.TENANT_DETAIL,
    {},
    {
      manual: true,
      onResponse(res) {
        if (res.code === 200) {
          localStorage.setItem("tenant", JSON.stringify(res.data));
          applyPageConfig();
        } else {
          message.error({
            content: res.msg,
            className: "znrunpay-merorder-error",
          });
        }
      },
    }
  );

  useEffect(() => {
    applyPageConfig();

    if (!localStorage.getItem("tenant")) {
      tenantDetailvoid({
        method: "post",
        data: {
          teantWebsite: window.location.origin,
        },
      });
    }
  }, []);

  //请求拦截器
  axios.interceptors.request.use(
    (config) => {
      const ts = Math.floor(Date.now() / 1000);
      const { method, params, data: body } = config;
      const query = {
        ts,
        app_key: APP_KEY,
        ...params,
      };
      for (var paramskey in query) {
        if (!query[paramskey]) {
          delete query[paramskey];
        }
      }
      const qs: string[] = [];
      const keys = Reflect.ownKeys(query).sort();

      for (const k of keys) qs.push(`${k as string}=${query[k]}`);
      if (method?.toLocaleUpperCase() === "POST") {
        const data = JSON.stringify(body);
        qs.push(`_param=${data.replace(/\s+/gi, "")}`);
      }
      const sign = MD5(qs.join("&"));
      config.params = query;
      config.headers["X-Req-Sign"] = sign;
      config.headers["token"] = getToken();
      const authStorage = localStorage.getItem("authorization");
      if (authStorage) {
        config.headers["authorization"] = "Bearer " + authStorage;
      }
      config.headers["Content-Type"] =
        method === "put" ? "multipart/form-data" : config.headers["Content-Type"];
      return config;
    },
    (error) => {
      return Promise.reject(error);
    }
  );

  axios.interceptors.response.use(
    function (response) {
      if (response.data.code === 401) {
        message.error(response.data.msg);
        if (!window.location.pathname.startsWith("/doc/")) {
          window.top?.location.assign("/sign/login");
        }
      }
      return response;
    },
    function (error) {
      const url = error?.config?.url || "";
      // blob 下载失败时由业务页解析错误体，避免这里弹出空 toast
      const isBlob = error?.config?.responseType === "blob";
      if (!isBlob && !url.includes("/admin/draforder/createdraf")) {
        const errMsg =
          error.response?.data?.msg ||
          error.response?.data?.message ||
          (error.response?.status === 404 ? "接口不存在" : "") ||
          "请求失败，请稍后重试";
        message.error({
          content: errMsg,
          className: "login-message-error",
        });
      }
      return Promise.reject(error);
    }
  );

  return <Suspense fallback={""}>{useRoutes(mainRoutes)}</Suspense>;
};

export default App;
